<template>
  <label class="ux-field-title" v-if="showLabel"
    >{{ label }}
    <span class="ux-required-marker" v-if="required">*</span></label
  >
  <div class="ux-field-description" v-if="showDescription">
    {{ description }}
  </div>

  <div v-if="multiValue">
    <flex-row
      class="ux-color-row"
      gap
      :key="index"
      v-for="(entry, index) in model"
    >
      <flex-cell>
        <ux-menu
          :modelValue="openKey === keyFor(index)"
          @update:modelValue="(v) => handleMenuUpdate(keyFor(index), v)"
          :closeOnClick="false"
          :group="instanceGuid"
        >
          <template #activator="{ on }">
            <div
              class="ux-color-swatch-wrap"
              :class="swatchClasses(model[index])"
              :style="swatchStyle(model[index])"
            >
              <button
                type="button"
                class="ux-color-swatch"
                @focus="touch"
                v-on="on"
              >
                <span class="ux-color-swatch-text">{{
                  model[index] || placeholder || "Select Color"
                }}</span>
              </button>
              <button
                v-if="hasValue(model[index])"
                type="button"
                class="ux-color-clear"
                aria-label="Clear colour"
                @click.stop="clearValue(index)"
              >
                <ux-icon icon="fa-times" />
              </button>
            </div>
          </template>
          <picker-popover
            :hsv="hsv"
            :alpha="alpha"
            :rgb="rgb"
            :alpha-percent="alphaPercent"
            :current-hex="currentHex"
            :current-display="currentDisplay"
            :presets="presets"
            :register-input="(el) => assignHexRef(keyFor(index), el)"
            @sv="applySvFromEvent"
            @hue="applyHueFromEvent"
            @alpha="applyAlphaFromEvent"
            @hex-input="onHexInput"
            @rgb-input="onRgbInput"
            @alpha-input="onAlphaInput"
            @preset="onPresetSelect"
            @close="close"
          />
        </ux-menu>
      </flex-cell>
      <flex-cell shrink vcenter>
        <ux-button tag="a" icon v-if="canRemoveValue" @click="remove(entry)">
          <ux-icon icon="fa-times" />
        </ux-button>
      </flex-cell>
    </flex-row>
    <ux-button v-if="canAddValue" @click="add()">{{ addLabel }}</ux-button>
  </div>
  <template v-else>
    <ux-menu
      :modelValue="openKey === 'single'"
      @update:modelValue="(v) => handleMenuUpdate('single', v)"
      :closeOnClick="false"
      :group="instanceGuid"
    >
      <template #activator="{ on }">
        <div
          class="ux-color-swatch-wrap"
          :class="swatchClasses(model)"
          :style="swatchStyle(model)"
        >
          <button
            type="button"
            class="ux-color-swatch"
            @focus="touch"
            v-on="on"
          >
            <span class="ux-color-swatch-text">{{ model || placeholder }}</span>
          </button>
          <div class="ux-color-clear">
            <ux-button
              icon
              size="sm"
              v-if="hasValue(model)"
              aria-label="Clear colour"
              @click.stop="clearValue()"
            >
              <ux-icon icon="fa-times" />
            </ux-button>
          </div>
        </div>
      </template>
      <picker-popover
        :hsv="hsv"
        :alpha="alpha"
        :rgb="rgb"
        :alpha-percent="alphaPercent"
        :current-hex="currentHex"
        :current-display="currentDisplay"
        :presets="presets"
        :register-input="(el) => assignHexRef('single', el)"
        @sv="applySvFromEvent"
        @hue="applyHueFromEvent"
        @alpha="applyAlphaFromEvent"
        @hex-input="onHexInput"
        @rgb-input="onRgbInput"
        @alpha-input="onAlphaInput"
        @preset="onPresetSelect"
        @close="close"
      />
    </ux-menu>
  </template>
</template>
<script>
import InputMixin from "./input-mixin";

//////////////////////////
// Colour conversion helpers

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function hexToRgba(hex) {
  let s = String(hex || "").trim();
  if (s.startsWith("#")) s = s.slice(1);
  if (s.length === 3 || s.length === 4) {
    s = s
      .split("")
      .map((c) => c + c)
      .join("");
  }
  if (s.length !== 6 && s.length !== 8) return null;
  const r = parseInt(s.slice(0, 2), 16);
  const g = parseInt(s.slice(2, 4), 16);
  const b = parseInt(s.slice(4, 6), 16);
  const a = s.length === 8 ? parseInt(s.slice(6, 8), 16) / 255 : 1;
  if ([r, g, b].some(Number.isNaN)) return null;
  return { r, g, b, a };
}

function rgbaToHex(r, g, b, a) {
  const c = (n) => clamp(Math.round(n), 0, 255).toString(16).padStart(2, "0");
  if (a == null || a >= 1) return `#${c(r)}${c(g)}${c(b)}`;
  return `#${c(r)}${c(g)}${c(b)}${c(a * 255)}`;
}

function rgbToHsv(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  let h = 0;
  if (d) {
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max ? d / max : 0, v: max };
}

function hsvToRgb(h, s, v) {
  const c = v * s;
  const hh = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  const m = v - c;
  let r = 0,
    g = 0,
    b = 0;
  if (hh < 1) [r, g, b] = [c, x, 0];
  else if (hh < 2) [r, g, b] = [x, c, 0];
  else if (hh < 3) [r, g, b] = [0, c, x];
  else if (hh < 4) [r, g, b] = [0, x, c];
  else if (hh < 5) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 };
}

function normaliseUserHex(input) {
  let hex = String(input || "").trim();
  if (!hex) return "";
  if (hex[0] === "#") hex = hex.slice(1);
  if (hex.length === 3)
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("");
  if (hex.length === 4)
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("");
  if (![6, 8].includes(hex.length)) return null;
  if (!/^[0-9a-fA-F]+$/.test(hex)) return null;
  return `#${hex.toLowerCase()}`;
}

const PRESETS = [
  "#d23a3a",
  "#e2913e",
  "#e8cf3f",
  "#8a572b",
  "#9fcd58",
  "#3f8a3c",
  "#b743be",
  "#7d3eb6",
  "#3b78d4",
  "#4dd1c1",
  "#c9e9a4",
  "#000000",
  "#444444",
  "#999999",
  "#dddddd",
  "#ffffff",
];

//////////////////////////
// Inline popover sub-component (kept in this file so the markup is one place
// while still being shared between single and multi-value templates).

const PickerPopover = {
  name: "picker-popover",
  props: {
    hsv: { type: Object, required: true },
    alpha: { type: Number, required: true },
    rgb: { type: Object, required: true },
    alphaPercent: { type: Number, required: true },
    currentHex: { type: String, default: "" },
    currentDisplay: { type: String, default: "" },
    presets: { type: Array, default: () => [] },
    registerInput: { type: Function, default: null },
  },
  emits: [
    "sv",
    "hue",
    "alpha",
    "hex-input",
    "rgb-input",
    "alpha-input",
    "preset",
    "close",
  ],
  methods: {
    startSv(event) {
      event.preventDefault();
      this.$emit("sv", { event, area: event.currentTarget, type: "start" });
    },
    startHue(event) {
      event.preventDefault();
      this.$emit("hue", { event, area: event.currentTarget, type: "start" });
    },
    startAlpha(event) {
      event.preventDefault();
      this.$emit("alpha", { event, area: event.currentTarget, type: "start" });
    },
    onKey(event) {
      if (event.key === "Enter" || event.key === "Escape") {
        event.preventDefault();
        this.$emit("close");
      }
    },
  },
  template: `
        <div class="ux-color-popover" @click.stop>
            <div
                class="ux-color-sv"
                :style="{ '--picker-hue': hsv.h }"
                @pointerdown="startSv"
            >
                <div
                    class="ux-color-sv-marker"
                    :style="{
                        left: (hsv.s * 100) + '%',
                        top: ((1 - hsv.v) * 100) + '%',
                    }"
                />
            </div>

            <div class="ux-color-controls">
                <div class="ux-color-sliders">
                    <div class="ux-color-hue" @pointerdown="startHue">
                        <div class="ux-color-slider-marker" :style="{ left: ((hsv.h / 360) * 100) + '%' }" />
                    </div>
                    <div
                        class="ux-color-alpha"
                        :style="{ '--alpha-color': currentHex }"
                        @pointerdown="startAlpha"
                    >
                        <div class="ux-color-alpha-fill" />
                        <div class="ux-color-slider-marker" :style="{ left: (alpha * 100) + '%' }" />
                    </div>
                </div>
                <div class="ux-color-preview">
                    <div class="ux-color-preview-fill" :style="{ background: currentDisplay }" />
                </div>
            </div>

            <div class="ux-color-inputs">
                <label class="ux-color-input-cell hex">
                    <input
                        type="text"
                        :value="currentHex.replace('#', '').toUpperCase()"
                        @input="$emit('hex-input', $event.target.value)"
                        @change="$emit('hex-input', $event.target.value, true)"
                        @keydown="onKey"
                        :ref="(el) => registerInput && registerInput(el)"
                    />
                    <span>Hex</span>
                </label>
                <label class="ux-color-input-cell">
                    <input
                        type="number"
                        min="0" max="255"
                        :value="Math.round(rgb.r)"
                        @input="$emit('rgb-input', { channel: 'r', value: $event.target.value })"
                        @keydown="onKey"
                    />
                    <span>R</span>
                </label>
                <label class="ux-color-input-cell">
                    <input
                        type="number"
                        min="0" max="255"
                        :value="Math.round(rgb.g)"
                        @input="$emit('rgb-input', { channel: 'g', value: $event.target.value })"
                        @keydown="onKey"
                    />
                    <span>G</span>
                </label>
                <label class="ux-color-input-cell">
                    <input
                        type="number"
                        min="0" max="255"
                        :value="Math.round(rgb.b)"
                        @input="$emit('rgb-input', { channel: 'b', value: $event.target.value })"
                        @keydown="onKey"
                    />
                    <span>B</span>
                </label>
                <label class="ux-color-input-cell">
                    <input
                        type="number"
                        min="0" max="100"
                        :value="alphaPercent"
                        @input="$emit('alpha-input', $event.target.value)"
                        @keydown="onKey"
                    />
                    <span>A</span>
                </label>
            </div>

            <div class="ux-color-presets">
                <button
                    v-for="preset in presets"
                    :key="preset"
                    type="button"
                    class="ux-color-preset"
                    :style="{ background: preset }"
                    @click="$emit('preset', preset)"
                />
            </div>
        </div>
    `,
};

//////////////////////////

let instanceCounter = 0;

export default {
  components: { PickerPopover },
  props: {
    modelValue: {
      type: String,
    },
  },
  mixins: [InputMixin],
  data() {
    return {
      // Tracks which swatch's popover is open. The ux-menu group prop
      // enforces mutual exclusion across this component instance, so
      // only one is ever set at a time.
      openKey: null,
      // Per-instance group key for ux-menu so two color pickers on the
      // same page don't fight each other through the global menu cache.
      instanceGuid: `ux-color-${++instanceCounter}`,
      hexInputs: {},
      // Internal HSV + alpha state used while interacting with the picker.
      // Sync'd from the model on open and on external changes; written
      // back as a hex string while dragging/typing.
      hsv: { h: 0, s: 0, v: 0 },
      alpha: 1,
      dragging: null, // 'sv' | 'hue' | 'alpha' | 'input' | null
      dragArea: null,
      presets: PRESETS,
    };
  },
  computed: {
    currentValue: {
      get() {
        if (this.openKey === "single") return this.model;
        if (
          typeof this.openKey === "string" &&
          this.openKey.startsWith("multi-")
        ) {
          const idx = parseInt(this.openKey.slice(6), 10);
          return Array.isArray(this.model) ? this.model[idx] : undefined;
        }
        return undefined;
      },
      set(v) {
        if (this.openKey === "single") {
          this.model = v;
        } else if (
          typeof this.openKey === "string" &&
          this.openKey.startsWith("multi-")
        ) {
          const idx = parseInt(this.openKey.slice(6), 10);
          if (Array.isArray(this.model)) this.model[idx] = v;
        }
      },
    },
    rgb() {
      return hsvToRgb(this.hsv.h, this.hsv.s, this.hsv.v);
    },
    currentHex() {
      return rgbaToHex(this.rgb.r, this.rgb.g, this.rgb.b, this.alpha);
    },
    currentDisplay() {
      return this.currentHex;
    },
    alphaPercent() {
      return Math.round(this.alpha * 100);
    },
  },
  watch: {
    currentValue: {
      immediate: true,
      handler(value) {
        if (this.dragging) return;
        const rgba = hexToRgba(value);
        if (!rgba) return;
        const hsv = rgbToHsv(rgba.r, rgba.g, rgba.b);
        if (hsv.s === 0 && this.hsv) {
          hsv.h = this.hsv.h;
        }
        this.hsv = hsv;
        this.alpha = rgba.a;
      },
    },
    openKey(newKey) {
      if (!newKey) return;
      // Focus + select the hex input once the popover slot has mounted.
      this.$nextTick(() => {
        const input = this.hexInputs[newKey];
        if (input) {
          input.focus();
          if (typeof input.select === "function") input.select();
        }
      });
    },
  },
  methods: {
    cleanInputValue(str) {
      return str;
    },
    cleanOutputValue(hex) {
      return hex;
    },
    commitHex(event, index) {
      const cleaned = normaliseUserHex(event?.target?.value);
      if (cleaned == null) return;
      if (typeof index === "number") this.model[index] = cleaned;
      else this.model = cleaned;
    },
    keyFor(index) {
      return `multi-${index}`;
    },
    hasValue(value) {
      return !!(value && String(value).length);
    },
    clearValue(index) {
      if (typeof index === "number") {
        this.model[index] = "";
      } else {
        this.model = "";
      }
    },
    assignHexRef(key, el) {
      if (el) this.hexInputs[key] = el;
      else delete this.hexInputs[key];
    },
    // Bridge between ux-menu's open state and our shared `openKey`. Each
    // ux-menu calls this with its own key — opening one auto-closes the
    // previously open one (via the menu group), and we mirror that into
    // openKey so currentValue resolves to the right slot.
    handleMenuUpdate(key, isOpen) {
      if (isOpen) {
        this.openKey = key;
      } else if (this.openKey === key) {
        this.openKey = null;
      }
    },
    close() {
      // Triggered by Enter/Escape inside the popover.
      if (this.openKey) this.openKey = null;
    },
    // ── SV / hue / alpha pointer interaction ───────────────────
    applySvFromEvent(payload) {
      this.touch();
      this.dragging = "sv";
      this.dragArea = payload.area;
      this._applySv(payload.event);
      document.addEventListener("pointermove", this.onPointerMove);
      document.addEventListener("pointerup", this.onPointerUp);
    },
    applyHueFromEvent(payload) {
      this.touch();
      this.dragging = "hue";
      this.dragArea = payload.area;
      this._applyHue(payload.event);
      document.addEventListener("pointermove", this.onPointerMove);
      document.addEventListener("pointerup", this.onPointerUp);
    },
    applyAlphaFromEvent(payload) {
      this.touch();
      this.dragging = "alpha";
      this.dragArea = payload.area;
      this._applyAlpha(payload.event);
      document.addEventListener("pointermove", this.onPointerMove);
      document.addEventListener("pointerup", this.onPointerUp);
    },
    onPointerMove(event) {
      if (this.dragging === "sv") this._applySv(event);
      else if (this.dragging === "hue") this._applyHue(event);
      else if (this.dragging === "alpha") this._applyAlpha(event);
    },
    onPointerUp() {
      this.dragging = null;
      this.dragArea = null;
      document.removeEventListener("pointermove", this.onPointerMove);
      document.removeEventListener("pointerup", this.onPointerUp);
    },
    _applySv(event) {
      const rect = this.dragArea?.getBoundingClientRect();
      if (!rect) return;
      const s = clamp((event.clientX - rect.left) / rect.width, 0, 1);
      const v = 1 - clamp((event.clientY - rect.top) / rect.height, 0, 1);
      this.hsv = { h: this.hsv.h, s, v };
      this.writeBack();
    },
    _applyHue(event) {
      const rect = this.dragArea?.getBoundingClientRect();
      if (!rect) return;
      const h = clamp((event.clientX - rect.left) / rect.width, 0, 1) * 360;
      this.hsv = { h, s: this.hsv.s, v: this.hsv.v };
      this.writeBack();
    },
    _applyAlpha(event) {
      const rect = this.dragArea?.getBoundingClientRect();
      if (!rect) return;
      this.alpha = clamp((event.clientX - rect.left) / rect.width, 0, 1);
      this.writeBack();
    },
    writeBack() {
      this.currentValue = this.currentHex;
    },
    // ── Manual input handlers ──────────────────────────────────
    onHexInput(rawValue, isCommit) {
      const cleaned = isCommit
        ? normaliseUserHex(rawValue)
        : `#${String(rawValue || "").replace("#", "")}`;
      if (!cleaned) return;
      const rgba = hexToRgba(cleaned);
      if (!rgba) return;
      this.dragging = "input";
      const hsv = rgbToHsv(rgba.r, rgba.g, rgba.b);
      if (hsv.s === 0) hsv.h = this.hsv.h;
      this.hsv = hsv;
      this.alpha = rgba.a;
      this.currentValue = this.currentHex;
      this.$nextTick(() => {
        this.dragging = null;
      });
    },
    onRgbInput({ channel, value }) {
      const n = clamp(parseInt(value, 10) || 0, 0, 255);
      const next = { ...this.rgb, [channel]: n };
      this.dragging = "input";
      const hsv = rgbToHsv(next.r, next.g, next.b);
      if (hsv.s === 0) hsv.h = this.hsv.h;
      this.hsv = hsv;
      this.currentValue = this.currentHex;
      this.$nextTick(() => {
        this.dragging = null;
      });
    },
    onAlphaInput(value) {
      const pct = clamp(parseInt(value, 10) || 0, 0, 100);
      this.dragging = "input";
      this.alpha = pct / 100;
      this.currentValue = this.currentHex;
      this.$nextTick(() => {
        this.dragging = null;
      });
    },
    onPresetSelect(hex) {
      const rgba = hexToRgba(hex);
      if (!rgba) return;
      this.dragging = "input";
      this.hsv = rgbToHsv(rgba.r, rgba.g, rgba.b);
      this.alpha = rgba.a;
      this.currentValue = this.currentHex;
      this.$nextTick(() => {
        this.dragging = null;
      });
    },
    // ── Validation + closed-swatch styling ─────────────────────
    isValidHex(value) {
      return !!hexToRgba(value);
    },
    swatchStyle(value) {
      if (!this.isValidHex(value)) return {};
      return { "--swatch-color": value };
    },
    swatchClasses(value) {
      return { "is-empty": !this.isValidHex(value) };
    },
    getNewDefaultEntry() {
      return "";
    },
  },
  beforeUnmount() {
    document.removeEventListener("pointermove", this.onPointerMove);
    document.removeEventListener("pointerup", this.onPointerUp);
  },
};
</script>
<style lang="scss" scoped>
.ux-color-row {
  margin-bottom: 0.5em;
}

// Wrapper carries the colour fill, auto-contrast text colour, and border.
// The inner swatch button + clear button both inherit from this so they pick
// up the right contrast colour on every value.
.ux-color-swatch-wrap {
  --swatch-color: transparent;
  position: relative;
  width: 100%;
  height: 2.5em;
  border-radius: var(--btn-border-radius);
  background: var(--swatch-color);
  background-color: var(
    --swatch-color,
    color-mix(in lab, currentColor 8%, transparent)
  );
  overflow: hidden;
  transition: box-shadow 0.15s;
  color: #{"oklch(from var(--swatch-color) clamp(0, calc((0.6 - l) * 100), 1) 0 0)"};

  border: 1px solid color-mix(in lab, currentColor 8%, transparent);
  &.is-empty {
    background: color-mix(in lab, currentColor 4%, transparent);
    color: inherit;
  }

  &:hover {
    border-color: color-mix(in lab, currentColor 15%, transparent);

    .ux-color-swatch-text {
      opacity: 0.6;
    }
  }
}

.ux-color-swatch {
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  // Right padding leaves room for the clear button (when present).
  padding: 0 3em 0 1em;
  background: transparent;
  border: none;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  font-weight: 500;
}

.ux-color-clear {
  position: absolute;
  top: 50%;
  right: 0.35em;
  transform: translateY(-50%);
  width: 1.7em;
  height: 1.7em;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  padding: 0;
  background: transparent;
  color: inherit;
  border-radius: 50%;
  cursor: pointer;
}

.ux-color-swatch-text {
  flex: 1;
  min-width: 0;
  letter-spacing: 0.02em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  opacity: 0.8;
}

// ── Popover (rendered inside ux-menu's panel; it positions/teleports for us) ──
.ux-color-popover {
  width: 280px;
  min-width: 280px;
  padding: 10px;
  border-radius: 8px;
  background: var(--bg1, #fff);
  color: inherit;
  user-select: none;
}

:deep(.ux-color-sv) {
  --picker-hue: 0;
  position: relative;
  width: 100%;
  height: 160px;
  border-radius: 5px;
  cursor: crosshair;
  touch-action: none;
  overflow: hidden;
  background: linear-gradient(to top, #000, transparent),
    linear-gradient(to right, #fff, hsl(var(--picker-hue), 100%, 50%));
  box-shadow: inset 0 0 0 1px color-mix(in lab, currentColor 14%, transparent);
}

:deep(.ux-color-sv-marker) {
  position: absolute;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow:
    0 0 0 1px color-mix(in lab, #000 70%, transparent),
    0 1px 2px color-mix(in lab, #000 30%, transparent);
  transform: translate(-50%, -50%);
  pointer-events: none;
}

:deep(.ux-color-controls) {
  display: flex;
  align-items: stretch;
  gap: 8px;
  margin-top: 8px;
}

:deep(.ux-color-sliders) {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: center;
}

:deep(.ux-color-hue) {
  position: relative;
  height: 12px;
  border-radius: 6px;
  cursor: ew-resize;
  touch-action: none;
  background: linear-gradient(
    to right,
    hsl(0, 100%, 50%),
    hsl(60, 100%, 50%),
    hsl(120, 100%, 50%),
    hsl(180, 100%, 50%),
    hsl(240, 100%, 50%),
    hsl(300, 100%, 50%),
    hsl(360, 100%, 50%)
  );
  box-shadow: inset 0 0 0 1px color-mix(in lab, currentColor 14%, transparent);
}

:deep(.ux-color-alpha) {
  --alpha-color: #000;
  position: relative;
  height: 12px;
  border-radius: 6px;
  cursor: ew-resize;
  touch-action: none;
  overflow: hidden;
  box-shadow: inset 0 0 0 1px color-mix(in lab, currentColor 14%, transparent);
  background-image: linear-gradient(
      45deg,
      #ccc 25%,
      transparent 25%,
      transparent 75%,
      #ccc 75%,
      #ccc
    ),
    linear-gradient(45deg, #ccc 25%, #fff 25%, #fff 75%, #ccc 75%, #ccc);
  background-size: 8px 8px;
  background-position:
    0 0,
    4px 4px;
}

:deep(.ux-color-alpha-fill) {
  position: absolute;
  inset: 0;
  background: linear-gradient(to right, transparent, var(--alpha-color));
  pointer-events: none;
}

:deep(.ux-color-slider-marker) {
  position: absolute;
  top: 50%;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow:
    0 0 0 1px color-mix(in lab, #000 70%, transparent),
    0 1px 2px color-mix(in lab, #000 30%, transparent);
  transform: translate(-50%, -50%);
  pointer-events: none;
}

:deep(.ux-color-preview) {
  flex-shrink: 0;
  width: 30px;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: inset 0 0 0 1px color-mix(in lab, currentColor 18%, transparent);
  background-image: linear-gradient(
      45deg,
      #ccc 25%,
      transparent 25%,
      transparent 75%,
      #ccc 75%,
      #ccc
    ),
    linear-gradient(45deg, #ccc 25%, #fff 25%, #fff 75%, #ccc 75%, #ccc);
  background-size: 8px 8px;
  background-position:
    0 0,
    4px 4px;
}

:deep(.ux-color-preview-fill) {
  width: 100%;
  height: 100%;
}

:deep(.ux-color-inputs) {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  gap: 4px;
  margin-top: 10px;
}

:deep(.ux-color-input-cell) {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  text-align: center;
  font-size: 0.78em;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.85;

  input {
    width: 100%;
    height: 26px;
    padding: 0 4px;
    border-radius: 4px;
    border: 1px solid color-mix(in lab, currentColor 18%, transparent);
    background: color-mix(in lab, currentColor 4%, transparent);
    color: inherit;
    font: inherit;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.95em;
    text-align: center;
    box-sizing: border-box;
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &:focus {
      border-color: var(--primary, currentColor);
      background: var(--bg1, #fff);
    }
  }

  span {
    margin-top: 2px;
    opacity: 0.7;
  }

  &.hex input {
    text-align: left;
    padding-left: 8px;
  }
}

:deep(.ux-color-presets) {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 4px;
  margin-top: 10px;
}

:deep(.ux-color-preset) {
  aspect-ratio: 1;
  border-radius: 4px;
  border: none;
  padding: 0;
  cursor: pointer;
  box-shadow: inset 0 0 0 1px color-mix(in lab, currentColor 18%, transparent);
  transition: transform 0.1s;

  &:hover {
    transform: scale(1.08);
    box-shadow:
      inset 0 0 0 1px color-mix(in lab, currentColor 35%, transparent),
      0 1px 4px color-mix(in lab, #000 20%, transparent);
  }
}
</style>
